import React, { useEffect, useId, useRef } from 'react';
import './portal-auth.css';

const iconPaths = {
  person: (
    <>
      <path d="M12 12.5a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" />
      <path d="M4.75 19a7.25 7.25 0 0 1 14.5 0" />
    </>
  ),
  email: (
    <>
      <path d="M4 6.5h16v11H4z" />
      <path d="m5 7.5 7 5 7-5" />
    </>
  ),
  lock: (
    <>
      <rect x="5.5" y="10" width="13" height="10" rx="2.5" />
      <path d="M8 10V8a4 4 0 1 1 8 0v2" />
    </>
  ),
  phone: (
    <>
      <path d="M8.5 4.5c.5-.5 1.2-.7 1.9-.5l1.7.6c.8.3 1.3 1 1.3 1.8v1.4c0 .5-.4.9-.9 1.1l-1.2.4a11.8 11.8 0 0 0 3.4 3.4l.4-1.2c.2-.5.6-.9 1.1-.9h1.4c.8 0 1.5.5 1.8 1.3l.6 1.7c.2.7 0 1.4-.5 1.9l-1.3 1.3c-.7.7-1.8 1-2.8.8-3-.6-5.8-2.1-8-4.4-2.3-2.2-3.8-5-4.4-8-.2-1 .1-2.1.8-2.8Z" />
    </>
  ),
  google: (
    <>
      <path d="M21.8 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.5a4.7 4.7 0 0 1-2 3.1v2.6h3.2c1.9-1.7 3.1-4.3 3.1-7.5Z" fill="currentColor" stroke="none" />
      <path d="M12 22c2.7 0 5-.9 6.7-2.4l-3.2-2.6c-.9.6-2 .9-3.5.9-2.7 0-4.9-1.8-5.7-4.2H3v2.7A10 10 0 0 0 12 22Z" fill="currentColor" stroke="none" />
      <path d="M6.3 13.7a6 6 0 0 1 0-3.4V7.6H3a10 10 0 0 0 0 8.8l3.3-2.7Z" fill="currentColor" stroke="none" />
      <path d="M12 6a5.4 5.4 0 0 1 3.8 1.5l2.8-2.8A9.6 9.6 0 0 0 12 2 10 10 0 0 0 3 7.6l3.3 2.7C7.1 7.8 9.3 6 12 6Z" fill="currentColor" stroke="none" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M3.5 3.5 20.5 20.5" />
      <path d="M8.2 8.2A4 4 0 0 0 12 16a4 4 0 0 0 2.2-.7" />
      <path d="M6.2 6.2C4 7.6 2.5 10 2.5 12c0 0 3.5 6 9.5 6 2 0 3.7-.5 5.1-1.3" />
      <path d="M17.6 17.6c2.1-1.4 3.9-3.6 3.9-5.6 0 0-3.5-6-9.5-6-1.3 0-2.5.2-3.6.6" />
    </>
  ),
  spinner: (
    <circle cx="12" cy="12" r="9" />
  )
};

export function PortalAuthIcon({ name, className = '' }) {
  const path = iconPaths[name];
  if (!path) return null;

  const isSpinner = name === 'google';
  return (
    <svg
      className={`portal-auth-icon ${className}`.trim()}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke={isSpinner ? 'none' : 'currentColor'}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}

export function PortalAuthLayout({
  company,
  loadingCompany = false,
  subtitle,
  children,
  cardWidth = '420px',
  cardClassName = ''
}) {
  const logo = company?.logoUrl || company?.logo || company?.logoURL || company?.imageUrl;

  return (
    <div className="portal-auth-shell">
      <div className="portal-auth-main" style={{ '--portal-auth-card-width': cardWidth }}>
        <header className="portal-auth-header">
          {loadingCompany ? (
            <div className="portal-auth-logo-skeleton portal-auth-skeleton" />
          ) : logo ? (
            <img className="portal-auth-logo" src={logo} alt={company?.name || 'Company logo'} />
          ) : (
            <div className="portal-auth-logo-fallback" aria-hidden="true">
              <PortalAuthIcon name="person" />
            </div>
          )}

          <div className="portal-auth-header-copy">
            {loadingCompany ? (
              <>
                <div className="portal-auth-title-skeleton portal-auth-skeleton" />
                <div className="portal-auth-subtitle-skeleton portal-auth-skeleton" />
              </>
            ) : (
              <>
                <h1 className="portal-auth-title">{company?.name || ''}</h1>
                {subtitle ? <p className="portal-auth-subtitle">{subtitle}</p> : null}
              </>
            )}
          </div>
        </header>

        <section className={`portal-auth-card ${cardClassName}`.trim()}>
          {children}
        </section>

        <p className="portal-auth-powered-by">
          Powered by{' '}
          <a href="https://neatschedule.com" target="_blank" rel="noopener noreferrer">
            Neat Schedule
          </a>
        </p>
      </div>
    </div>
  );
}

export function PortalAuthStandalone({ title, message, action, children }) {
  return (
    <div className="portal-auth-shell portal-auth-shell--centered">
      <div className="portal-auth-main" style={{ '--portal-auth-card-width': '420px' }}>
        <section className="portal-auth-card portal-auth-card--centered">
          <div className="portal-auth-status">
            <h2 className="portal-auth-status-title">{title}</h2>
            {message ? <p className="portal-auth-status-message">{message}</p> : null}
            {children}
            {action ? <div className="portal-auth-status-actions">{action}</div> : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export function PortalAuthNotice({ children, tone = 'error' }) {
  if (!children) return null;
  return (
    <div className={`portal-auth-notice portal-auth-notice--${tone}`}>
      {children}
    </div>
  );
}

export function PortalAuthField({
  id,
  label,
  helperText,
  error = false,
  startIcon,
  endAdornment,
  className = '',
  inputClassName = '',
  ...inputProps
}) {
  const generatedId = useId();
  const resolvedId = id || generatedId;

  return (
    <div className={`portal-auth-field ${className}`.trim()}>
      {label ? (
        <label className="portal-auth-label" htmlFor={resolvedId}>
          {label}
        </label>
      ) : null}
      <div className={`portal-auth-input-wrap ${error ? 'is-error' : ''}`.trim()}>
        {startIcon ? (
          <span className="portal-auth-input-icon" aria-hidden="true">
            <PortalAuthIcon name={startIcon} />
          </span>
        ) : null}
        <input
          id={resolvedId}
          className={`portal-auth-input ${inputClassName}`.trim()}
          aria-invalid={error || undefined}
          {...inputProps}
        />
        {endAdornment ? <div className="portal-auth-input-end">{endAdornment}</div> : null}
      </div>
      {helperText ? (
        <p className={`portal-auth-helper ${error ? 'is-error' : ''}`.trim()}>{helperText}</p>
      ) : null}
    </div>
  );
}

export function PortalAuthCheckbox({ id, checked, onChange, name, children }) {
  const generatedId = useId();
  const resolvedId = id || generatedId;

  return (
    <label className="portal-auth-checkbox" htmlFor={resolvedId}>
      <input
        id={resolvedId}
        className="portal-auth-checkbox-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        name={name}
      />
      <span className="portal-auth-checkbox-box" aria-hidden="true">
        <span className="portal-auth-checkbox-check" />
      </span>
      <span className="portal-auth-checkbox-text">{children}</span>
    </label>
  );
}

export function PortalAuthButton({
  variant = 'primary',
  loading = false,
  icon,
  className = '',
  children,
  ...props
}) {
  return (
    <button
      className={`portal-auth-button portal-auth-button--${variant} ${className}`.trim()}
      {...props}
    >
      {loading ? (
        <span className="portal-auth-button-spinner" aria-hidden="true" />
      ) : icon ? (
        <span className="portal-auth-button-icon" aria-hidden="true">
          <PortalAuthIcon name={icon} />
        </span>
      ) : null}
      <span>{children}</span>
    </button>
  );
}

export function PortalAuthDivider({ label = 'OR' }) {
  return (
    <div className="portal-auth-divider" aria-hidden="true">
      <span className="portal-auth-divider-line" />
      <span className="portal-auth-divider-label">{label}</span>
      <span className="portal-auth-divider-line" />
    </div>
  );
}

export function PortalAuthStatus({ title, message, busy = false }) {
  return (
    <div className="portal-auth-status">
      {busy ? <div className="portal-auth-status-spinner" aria-hidden="true" /> : null}
      <h2 className="portal-auth-status-title">{title}</h2>
      {message ? <p className="portal-auth-status-message">{message}</p> : null}
    </div>
  );
}

export function PortalAuthFormSkeleton({ variant = 'login' }) {
  return (
    <div className="portal-auth-skeleton-stack">
      <div className="portal-auth-input-skeleton portal-auth-skeleton" />
      <div className="portal-auth-input-skeleton portal-auth-skeleton" />
      {variant === 'register' ? (
        <>
          <div className="portal-auth-input-skeleton portal-auth-skeleton" />
          <div className="portal-auth-input-skeleton portal-auth-skeleton" />
          <div className="portal-auth-input-skeleton portal-auth-skeleton" />
          <div className="portal-auth-input-skeleton portal-auth-skeleton" />
        </>
      ) : null}
      <div className="portal-auth-row-skeleton">
        <div className="portal-auth-text-skeleton portal-auth-skeleton" />
        <div className="portal-auth-text-skeleton portal-auth-skeleton portal-auth-text-skeleton--short" />
      </div>
      <div className="portal-auth-button-skeleton portal-auth-skeleton" />
      <div className="portal-auth-divider" aria-hidden="true">
        <span className="portal-auth-divider-line" />
        <span className="portal-auth-divider-label">OR</span>
        <span className="portal-auth-divider-line" />
      </div>
      <div className="portal-auth-button-skeleton portal-auth-skeleton portal-auth-button-skeleton--secondary" />
    </div>
  );
}

export function PortalAuthModal({ open, title, onClose, children, actions }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      const firstFocusable = dialogRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="portal-auth-modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className="portal-auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-auth-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="portal-auth-modal-header">
          <h2 id="portal-auth-modal-title" className="portal-auth-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="portal-auth-modal-close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="portal-auth-modal-content">{children}</div>
        {actions ? <div className="portal-auth-modal-actions">{actions}</div> : null}
      </div>
    </div>
  );
}
